import { useCallback, useMemo } from "react";
import { useMediaDataRoom } from "features/mediaDataRoom/contexts/MediaDataRoomContext";
import { useMediaDataRoomInsightsData } from "features/mediaDataRoom/contexts/MediaDataRoomInsightsDataContext";
import {
  MediaDataRoomJobInput,
  useMediaDataRoomJob,
} from "features/mediaDataRoom/hooks/v3";
import {
  type AudienceV3,
  MediaDataRoomVersion,
  type SeedAudienceV3,
} from "features/mediaDataRoom/models";
import { type LookalikeAudienceStatisticsHookResult } from "./models";
import useLookalikeAudienceStatisticsState from "./useLookalikeAudienceStatisticsState";
import { transform } from "./utils";

interface LookalikeAudienceStatisticsHookPayload {
  audienceId: string | undefined;
  audiences: AudienceV3[] | undefined;
  seedAudiences: SeedAudienceV3[] | undefined;
  audienceReach: number;
  skip?: boolean;
  excludeSeedAudience?: boolean;
}

export const useLookalikeAudienceStatistics = ({
  audienceId,
  audiences,
  seedAudiences,
  audienceReach,
  skip,
  excludeSeedAudience = false,
}: LookalikeAudienceStatisticsHookPayload): LookalikeAudienceStatisticsHookResult => {
  const { dataRoomId } = useMediaDataRoom();
  const { session, publishedDatasetsHashes } = useMediaDataRoomInsightsData(
    MediaDataRoomVersion.v3
  );
  const audienceRef = useMemo(() => {
    const audience = [...(audiences ?? []), ...(seedAudiences ?? [])].find(
      (a) => a.id === audienceId
    );
    return {
      audienceRef: {
        kind:
          audience?.kind === "seed" ? ("SEED" as const) : ("CUSTOM" as const),
        ...(audience?.kind === "seed"
          ? {
              audienceType: audience.id,
            }
          : { id: audienceId }),
      },
    };
  }, [audienceId, audiences, seedAudiences]);
  const computeJob = useMediaDataRoomJob({
    input: MediaDataRoomJobInput.create(
      "getLookalikeAudienceStatistics",
      dataRoomId,
      publishedDatasetsHashes
    ).withResourceId(audienceId!),
    requestCreator: useCallback(() => audienceRef, [audienceRef]),
    session,
    skip: skip || !audienceId,
    transform,
  });
  return useLookalikeAudienceStatisticsState({
    ...computeJob,
    audienceId,
    audienceReach,
    excludeSeedAudience,
  });
};
